import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Camera, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/ui/dialog";
import { PhotoCapture } from "@/components/photo-capture";
import { SearchableCombobox } from "@/components/searchable-combobox";
import { NotOnListNote, modelOffList } from "@/components/model-tap-list";
import { downscaleDataUrl } from "@/lib/downscale-image";
import { mergeSizeOptions } from "@/lib/assembly-sizes";
import { ASSEMBLY_TYPE_OPTIONS, type AssemblyType } from "@/lib/vocabulary";

// ─── Canonical backflow assembly types ───────────────────────────────
// The vocabulary itself lives in @/lib/vocabulary (single source, shared
// with plain-English names); re-exported here so the field tech workflow
// and the office "Add device" modal keep their existing import paths.
export type { AssemblyType } from "@/lib/vocabulary";

export const ASSEMBLY_TYPES = ASSEMBLY_TYPE_OPTIONS;

export const ASSEMBLY_LABEL: Record<string, string> = Object.fromEntries(ASSEMBLY_TYPES.map((t) => [t.value, t.label]));

const VALID_ASSEMBLY_VALUES = new Set(ASSEMBLY_TYPES.map((t) => t.value));

/** Detector assemblies (carry a bypass meter) — same set the tech form uses. */
export function isDetectorAssembly(t: string): boolean {
    return t === "dcda" || t === "dcda_ii" || t === "rpda" || t === "rpda_ii";
}

type OcrConfidenceLevel = "high" | "medium" | "low";
type OcrFieldKey = "assemblyType" | "sizeLabel" | "manufacturer" | "model" | "serialNumber" | "pipeMaterial";

function ocrFieldRingClass(level: OcrConfidenceLevel | null | undefined, filled: boolean, ocrRan: boolean): string {
    if (level === "medium") return "ring-2 ring-amber-400 rounded-md";
    if (level === "low" || (ocrRan && !filled)) return "ring-2 ring-red-400 rounded-md";
    return "";
}

export interface BackflowDeviceFieldValues {
    assemblyType: string;
    sizeLabel: string;
    manufacturer: string;
    model: string;
    serialNumber: string;
    /** What the assembly is plumbed into. "" means nobody has answered —
     *  which is a real state, never resolved to the commonest material. */
    pipeMaterial?: string;
    /** How we came to believe it. Set to `vision_confirmed` only when the
     *  tech accepts a scanned proposal; typing it makes it `human`. */
    pipeMaterialSource?: "vision_confirmed" | "human";
    /** The reader's confidence when it proposed, carried so an accepted
     *  low-confidence guess stays visible as one. */
    pipeMaterialConfidence?: "high" | "medium" | "low";
}

/**
 * What a scanned pipe material is allowed to do.
 *
 * Held to a stricter bar than the tag fields beside it, which pre-fill at
 * anything above `low`. A wrong material silently changes what a customer is
 * charged — up to $1,161.76 at 2" — and nothing else on the form corroborates
 * it, the way a legible serial corroborates itself. So only `high` pre-fills;
 * anything less is offered as a suggestion the tech has to actively accept,
 * and `unknown` (the normal answer on a tag close-up showing no pipe) is
 * ignored rather than surfaced as noise on every scan.
 *
 * Exported so the test asserts THIS function rather than a copy of its rules.
 */
export function decidePipeMaterialFromScan(
    pipeMaterial: string | null,
    confidence: OcrConfidenceLevel | null,
):
    | { action: "prefill"; value: string; source: "vision_confirmed" }
    | { action: "suggest"; value: string; confidence: OcrConfidenceLevel }
    | { action: "ignore" } {
    const usable = !!pipeMaterial && pipeMaterial !== "unknown";
    if (!usable) return { action: "ignore" };
    if (confidence === "high") return { action: "prefill", value: pipeMaterial, source: "vision_confirmed" };
    // A model that omitted its confidence has not told us it is sure.
    return { action: "suggest", value: pipeMaterial, confidence: confidence ?? "low" };
}

/** The quoter's five, plus the empty "not answered" state. */
export const PIPE_MATERIAL_OPTIONS: Array<{ value: string; label: string }> = [
    { value: "", label: "Not recorded" },
    { value: "copper_press", label: "Copper (press)" },
    { value: "pvc_40", label: "PVC schedule 40" },
    { value: "pvc_80", label: "PVC schedule 80" },
    { value: "cpvc", label: "CPVC" },
    { value: "uponor", label: "Uponor / PEX" },
];

/**
 * Real-database device identity picker (assembly type, size, make, model,
 * serial) — the exact cascade the field tech sees: size → manufacturer →
 * model are populated from `/api/device-cascade` (the USC-approved
 * assembly catalog), with USC install-status hints, a "show legacy"
 * toggle, and a Scan Tag OCR auto-fill. Controlled via `value`/`onChange`
 * so any parent (field workflow, office modal) can embed it.
 */
export function BackflowDeviceFields({
    value,
    onChange,
    siteId,
    showScanTag = true,
}: {
    value: BackflowDeviceFieldValues;
    onChange: (patch: Partial<BackflowDeviceFieldValues>) => void;
    siteId: string;
    showScanTag?: boolean;
}) {
    const [showLegacyDevices, setShowLegacyDevices] = useState(false);
    const [scanTagOpen, setScanTagOpen] = useState(false);
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrRan, setOcrRan] = useState(false);
    const [ocrConfidence, setOcrConfidence] = useState<Partial<Record<OcrFieldKey, OcrConfidenceLevel>>>({});
    const [ocrDuplicate, setOcrDuplicate] = useState<{ id: string; label: string } | null>(null);
    // A material the reader proposed but was not sure enough to fill in. Held
    // beside the field as a one-tap suggestion rather than written to the form.
    const [pipeMaterialSuggestion, setPipeMaterialSuggestion] = useState<{
        value: string;
        confidence: OcrConfidenceLevel;
    } | null>(null);

    // The cascade endpoint keys on a normalized assembly type (the
    // "_II" detector variants share the base catalog).
    const cascadeType = value.assemblyType.toUpperCase().replace("_II", "");
    const cascade = useQuery({
        queryKey: ["device-cascade", cascadeType, value.sizeLabel, value.manufacturer, showLegacyDevices],
        queryFn: async () => {
            const params = new URLSearchParams({ assemblyType: cascadeType });
            if (value.sizeLabel) params.set("size", value.sizeLabel);
            if (value.manufacturer) params.set("manufacturer", value.manufacturer);
            if (showLegacyDevices) params.set("includeNonInstallable", "true");
            return api.get<{
                sizes: string[];
                manufacturers: string[];
                models: string[];
                modelRows?: Array<{ model: string; usc_install_status: string | null }>;
            }>(`/api/device-cascade?${params}`);
        },
        enabled: !!value.assemblyType,
        staleTime: 60_000,
    });

    const sizes = mergeSizeOptions(cascade.data?.sizes ?? []);
    const manufacturers = cascade.data?.manufacturers ?? [];
    const models = cascade.data?.models ?? [];
    const modelRows = cascade.data?.modelRows ?? [];
    const modelStatusByName = useMemo(() => {
        const map: Record<string, string | null> = {};
        for (const r of modelRows) map[r.model] = r.usc_install_status;
        return map;
    }, [modelRows]);
    const selectedModelStatus = value.model ? (modelStatusByName[value.model] ?? null) : null;

    const handleOcrCapture = async (dataUrl: string) => {
        setScanTagOpen(false);
        setOcrLoading(true);
        setOcrDuplicate(null);
        try {
            const scaled = await downscaleDataUrl(dataUrl);
            const res = await api.post<{
                assemblyType: string;
                size: string | null;
                manufacturer: string | null;
                model: string | null;
                serial: string | null;
                confidence: {
                    assembly_type: OcrConfidenceLevel | null;
                    size: OcrConfidenceLevel | null;
                    manufacturer: OcrConfidenceLevel | null;
                    model: OcrConfidenceLevel | null;
                    serial: OcrConfidenceLevel | null;
                    pipe_material: OcrConfidenceLevel | null;
                };
                pipeMaterial: string | null;
                matchedAssetId: string | null;
                matchedAssetLabel: string | null;
            }>("/api/assets/ocr-tag", {
                image: scaled.dataUrl,
                siteId,
                width: scaled.width,
                height: scaled.height,
            });

            const confMap: Partial<Record<OcrFieldKey, OcrConfidenceLevel>> = {};
            const patch: Partial<BackflowDeviceFieldValues> = {};

            // Only the free-text tag fields. Narrowed deliberately: the
            // provenance keys added for pipe material are unions, not strings,
            // and must never be written by this generic path.
            type TextField = "assemblyType" | "sizeLabel" | "manufacturer" | "model" | "serialNumber";
            const applyText = (
                key: OcrFieldKey,
                conf: OcrConfidenceLevel | null,
                val: string | null,
                field: TextField,
            ) => {
                if (!val || conf === "low") {
                    confMap[key] = conf ?? "low";
                    return;
                }
                confMap[key] = conf ?? "high";
                patch[field] = val;
            };

            const asmConf = res.confidence.assembly_type;
            if (
                res.assemblyType &&
                res.assemblyType !== "unknown" &&
                VALID_ASSEMBLY_VALUES.has(res.assemblyType as AssemblyType) &&
                asmConf !== "low"
            ) {
                confMap.assemblyType = asmConf ?? "high";
                patch.assemblyType = res.assemblyType;
            } else if (asmConf) {
                confMap.assemblyType = asmConf === "high" ? "low" : asmConf;
            } else {
                confMap.assemblyType = "low";
            }

            applyText("sizeLabel", res.confidence.size, res.size, "sizeLabel");
            applyText("manufacturer", res.confidence.manufacturer, res.manufacturer, "manufacturer");
            applyText("model", res.confidence.model, res.model, "model");
            applyText("serialNumber", res.confidence.serial, res.serial, "serialNumber");

            // Pipe material is held to a STRICTER bar than the tag fields
            // above, which pre-fill at anything above `low`. Here only `high`
            // pre-selects, because a wrong material is worth up to $1,161.76
            // at 2" and nothing on the tag itself corroborates it — unlike a
            // serial or model, a tech cannot glance at the form and see that
            // the reader misread. Anything less is shown as a suggestion the
            // tech has to actively pick.
            const pm = decidePipeMaterialFromScan(res.pipeMaterial, res.confidence.pipe_material);
            if (pm.action === "prefill") {
                confMap.pipeMaterial = "high";
                patch.pipeMaterial = pm.value;
                // Accepting a scan is what makes it `vision_confirmed`; the
                // tech still has to save the form, which is the confirmation.
                patch.pipeMaterialSource = pm.source;
                patch.pipeMaterialConfidence = "high";
                setPipeMaterialSuggestion(null);
            } else if (pm.action === "suggest") {
                // Proposed but NOT applied. Surfaced beside the field; the
                // value stays empty until the tech chooses it.
                confMap.pipeMaterial = pm.confidence;
                setPipeMaterialSuggestion({ value: pm.value, confidence: pm.confidence });
            } else {
                setPipeMaterialSuggestion(null);
            }

            onChange(patch);
            setOcrConfidence(confMap);
            setOcrRan(true);
            if (res.matchedAssetId) {
                setOcrDuplicate({
                    id: res.matchedAssetId,
                    label: res.matchedAssetLabel ?? res.matchedAssetId,
                });
            }
        } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Could not read tag — enter manually.");
            setOcrRan(true);
        } finally {
            setOcrLoading(false);
        }
    };

    return (
        <>
            <div className="space-y-3">
                {showScanTag && (
                    <div className="space-y-1">
                        <Button
                            type="button"
                            className="w-full h-11 gap-2"
                            onClick={() => setScanTagOpen(true)}
                            disabled={ocrLoading}
                        >
                            {ocrLoading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Reading tag…
                                </>
                            ) : (
                                <>
                                    <Camera className="h-4 w-4" />
                                    Scan Tag
                                </>
                            )}
                        </Button>
                        {ocrDuplicate && (
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                Serial may match existing device on site: {ocrDuplicate.label}
                            </p>
                        )}
                    </div>
                )}

                <div>
                    <label className="text-xs font-medium">Assembly Type</label>
                    <div
                        className={cn(
                            "mt-1",
                            ocrFieldRingClass(ocrConfidence.assemblyType, !!value.assemblyType, ocrRan),
                        )}
                    >
                        <select
                            value={value.assemblyType}
                            onChange={(e) =>
                                onChange({
                                    assemblyType: e.target.value,
                                    sizeLabel: "",
                                    manufacturer: "",
                                    model: "",
                                })
                            }
                            className="w-full h-12 rounded-md border bg-background px-3 text-sm"
                        >
                            <option value="">— Select —</option>
                            {ASSEMBLY_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium">Size</label>
                    <div
                        className={cn(
                            "mt-1",
                            ocrFieldRingClass(ocrConfidence.sizeLabel, !!value.sizeLabel.trim(), ocrRan),
                        )}
                    >
                        <SearchableCombobox
                            value={value.sizeLabel}
                            onChange={(v) => onChange({ sizeLabel: v, manufacturer: "", model: "" })}
                            options={sizes}
                            placeholder="Select size…"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium">Manufacturer</label>
                    <div
                        className={cn(
                            "mt-1",
                            ocrFieldRingClass(ocrConfidence.manufacturer, !!value.manufacturer.trim(), ocrRan),
                        )}
                    >
                        <SearchableCombobox
                            value={value.manufacturer}
                            onChange={(v) => onChange({ manufacturer: v, model: "" })}
                            options={manufacturers}
                            placeholder={value.sizeLabel ? "Select manufacturer…" : "Choose size first"}
                            disabled={!value.sizeLabel}
                            allowCreate
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium">Model</label>
                    <div className={cn("mt-1", ocrFieldRingClass(ocrConfidence.model, !!value.model.trim(), ocrRan))}>
                        <SearchableCombobox
                            value={value.model}
                            onChange={(v) => onChange({ model: v })}
                            options={models}
                            placeholder={value.manufacturer ? "Tap the model…" : "Choose manufacturer first"}
                            disabled={!value.manufacturer}
                            allowCreate
                        />
                        {/* Device check (11/2): a model off the list is said out loud. */}
                        <NotOnListNote show={modelOffList(value.model, models, !!cascade.data)} />
                        {/* #1503 — this is a heads-up, not a stop sign. The save
                            goes through and the office gets the note; telling the
                            tech "saving will be blocked" was both wrong and the
                            reason a real device on the wall couldn't be written
                            down. The compliance question is still raised, it just
                            no longer holds up the job. */}
                        {selectedModelStatus && selectedModelStatus !== "installable" && (
                            <div className="text-xs mt-1 text-amber-700 dark:text-amber-300">
                                ⚠ This model is{" "}
                                {selectedModelStatus === "legacy_edition"
                                    ? "on an older USC manual — not approved for a new install"
                                    : selectedModelStatus === "spare_parts_only"
                                      ? "spare-parts-only — not approved for a new install"
                                      : selectedModelStatus === "expired_renewal"
                                        ? "past its USC renewal — not approved for a new install"
                                        : "not approved for a new install"}
                                . Record it anyway if that's what's on the wall — the office gets a note to review.
                            </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                            <label className="text-xs text-foreground/80 inline-flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5"
                                    checked={showLegacyDevices}
                                    onChange={(e) => setShowLegacyDevices(e.target.checked)}
                                />
                                Show legacy / spare-parts devices
                            </label>
                        </div>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium">Serial Number</label>
                    <div
                        className={cn(
                            "mt-1",
                            ocrFieldRingClass(ocrConfidence.serialNumber, !!value.serialNumber.trim(), ocrRan),
                        )}
                    >
                        <Input
                            value={value.serialNumber}
                            onChange={(e) => onChange({ serialNumber: e.target.value })}
                            placeholder="Enter serial number"
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs font-medium">Pipe material</label>
                    <div
                        className={cn(
                            "mt-1",
                            ocrFieldRingClass(ocrConfidence.pipeMaterial, !!value.pipeMaterial, ocrRan),
                        )}
                    >
                        <select
                            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                            value={value.pipeMaterial ?? ""}
                            onChange={(e) =>
                                onChange(
                                    e.target.value
                                        ? {
                                              pipeMaterial: e.target.value,
                                              // Chosen by a person looking at the pipe.
                                              pipeMaterialSource: "human",
                                              pipeMaterialConfidence: undefined,
                                          }
                                        : // Clearing it returns the device to "nobody
                                          // has answered", which is a real state.
                                          {
                                              pipeMaterial: "",
                                              pipeMaterialSource: undefined,
                                              pipeMaterialConfidence: undefined,
                                          },
                                )
                            }
                        >
                            {PIPE_MATERIAL_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {pipeMaterialSuggestion && !value.pipeMaterial && (
                        <button
                            type="button"
                            className="mt-1 text-left text-[11px] text-foreground/70 underline"
                            onClick={() => {
                                onChange({
                                    pipeMaterial: pipeMaterialSuggestion.value,
                                    pipeMaterialSource: "vision_confirmed",
                                    pipeMaterialConfidence: pipeMaterialSuggestion.confidence,
                                });
                                setPipeMaterialSuggestion(null);
                            }}
                        >
                            The scan thought{" "}
                            <strong>
                                {PIPE_MATERIAL_OPTIONS.find((o) => o.value === pipeMaterialSuggestion.value)?.label ??
                                    pipeMaterialSuggestion.value}
                            </strong>{" "}
                            ({pipeMaterialSuggestion.confidence} confidence) — tap to use it
                        </button>
                    )}
                    <p className="mt-1 text-[11px] text-foreground/60">
                        What the assembly is plumbed into. Leave it unset rather than guess — blank means "nobody has
                        looked", and never stands in for a material.
                    </p>
                </div>
            </div>

            <Dialog open={scanTagOpen} onClose={() => setScanTagOpen(false)} title="Scan device tag">
                <PhotoCapture
                    onCapture={(dataUrl) => void handleOcrCapture(dataUrl)}
                    onCancel={() => setScanTagOpen(false)}
                />
            </Dialog>
        </>
    );
}
